/**
 * Native Modal System
 * Replaces Webflow modals with vanilla JavaScript
 */

class NativeModals {
  constructor() {
    this.activeModal = null;
    this.init();
  }

  init() {
    this.setupModalTriggers();
    this.setupModalClose();
    this.setupKeyboardNavigation();
  }

  setupModalTriggers() {
    // Handle data-modal triggers
    const modalTriggers = document.querySelectorAll('[data-modal]');
    
    modalTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = trigger.getAttribute('data-modal');
        this.openModal(modalId);
      });
    });

    // Handle class-based triggers
    const classTriggers = document.querySelectorAll('.modal-trigger');
    
    classTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        const modalId = trigger.getAttribute('href')?.replace('#', '') || 
                        trigger.getAttribute('data-target')?.replace('#', '');
        if (modalId) {
          this.openModal(modalId);
        }
      });
    });
  }

  setupModalClose() {
    // Close buttons
    const closeButtons = document.querySelectorAll('.modal-close, [data-modal-close]');
    
    closeButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.closeModal();
      });
    });

    // Overlay click to close
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('modal-overlay')) {
        this.closeModal();
      }
    });
  }

  setupKeyboardNavigation() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.activeModal) {
        this.closeModal();
      }
    });
  }

  openModal(modalId) {
    const modal = document.getElementById(modalId);
    if (!modal) {
      console.error(`Modal with ID "${modalId}" not found`);
      return;
    }

    // Close any existing modal
    if (this.activeModal) {
      this.closeModal();
    }

    this.activeModal = modal;
    
    // Show modal
    modal.classList.add('active');
    document.body.classList.add('modal-open');
    
    // Focus first focusable element
    const firstFocusable = modal.querySelector('input, button, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (firstFocusable) {
      firstFocusable.focus();
    }

    // Trap focus within modal
    this.trapFocus(modal);

    // Trigger custom event
    modal.dispatchEvent(new CustomEvent('modal:open', { detail: { modalId } }));
  }

  closeModal() {
    if (!this.activeModal) return;

    const modal = this.activeModal;
    
    // Hide modal
    modal.classList.remove('active');
    document.body.classList.remove('modal-open');
    
    // Remove focus trap
    this.removeFocusTrap();

    // Trigger custom event
    modal.dispatchEvent(new CustomEvent('modal:close', { detail: { modalId: modal.id } }));

    this.activeModal = null;
  }

  trapFocus(modal) {
    const focusableElements = modal.querySelectorAll(
      'input, button, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
    );
    
    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];

    modal.addEventListener('keydown', this.handleFocusTrap);

    this.focusTrapHandler = (e) => {
      if (e.key === 'Tab') {
        if (e.shiftKey) {
          if (document.activeElement === firstFocusable) {
            e.preventDefault();
            lastFocusable.focus();
          }
        } else {
          if (document.activeElement === lastFocusable) {
            e.preventDefault();
            firstFocusable.focus();
          }
        }
      }
    };
  }

  removeFocusTrap() {
    if (this.activeModal && this.focusTrapHandler) {
      this.activeModal.removeEventListener('keydown', this.focusTrapHandler);
      this.focusTrapHandler = null;
    }
  }

  // Utility method to create modal programmatically
  createModal(options = {}) {
    const {
      id = 'modal-' + Date.now(),
      title = '',
      content = '',
      size = 'medium',
      closable = true
    } = options;

    const modal = document.createElement('div');
    modal.id = id;
    modal.className = `modal modal-${size}`;
    modal.innerHTML = `
      <div class="modal-overlay"></div>
      <div class="modal-content">
        <div class="modal-header">
          ${title ? `<h3 class="modal-title">${title}</h3>` : ''}
          ${closable ? '<button class="modal-close" aria-label="Close modal">&times;</button>' : ''}
        </div>
        <div class="modal-body">
          ${content}
        </div>
      </div>
    `;

    document.body.appendChild(modal);
    return modal;
  }

  // Utility method to show alert
  showAlert(message, type = 'info') {
    const modal = this.createModal({
      title: type.charAt(0).toUpperCase() + type.slice(1),
      content: `<p>${message}</p>`,
      size: 'small'
    });

    this.openModal(modal.id);
    
    // Auto-close after 3 seconds
    setTimeout(() => {
      this.closeModal();
      modal.remove();
    }, 3000);
  }

  // Utility method to show confirmation
  showConfirm(message, onConfirm, onCancel) {
    const modal = this.createModal({
      title: 'Confirm',
      content: `
        <p>${message}</p>
        <div class="modal-actions">
          <button class="btn btn-secondary" data-action="cancel">Cancel</button>
          <button class="btn btn-primary" data-action="confirm">Confirm</button>
        </div>
      `,
      size: 'small'
    });

    this.openModal(modal.id);

    // Handle button clicks
    modal.addEventListener('click', (e) => {
      if (e.target.dataset.action === 'confirm') {
        this.closeModal();
        modal.remove();
        if (onConfirm) onConfirm();
      } else if (e.target.dataset.action === 'cancel') {
        this.closeModal();
        modal.remove();
        if (onCancel) onCancel();
      }
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.nativeModals = new NativeModals();
});

// Export for use in other scripts
window.NativeModals = NativeModals;
